import React from 'react';
import { Calendar, CheckCircle, Target, MessageSquare, TrendingUp, Star } from 'lucide-react';
import type { Week, Todo, ReviewEntry } from '../types';
import { parseUTCTimestamp } from '../utils/dateUtils';

interface WeekSummaryProps {
  week: Week;
  todayTasks: Todo[];
  activeWeekTasks: Todo[];
  draftTasks: Todo[];
  onStartWeeklyReview: () => void;
}

const WeekSummary: React.FC<WeekSummaryProps> = ({
  week,
  todayTasks,
  activeWeekTasks,
  draftTasks,
  onStartWeeklyReview
}) => {
  const formatWeekRange = (week: Week) => {
    const start = new Date(week.start_date);
    const end = new Date(week.end_date);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const getTaskStats = () => {
    const allTasks = [...todayTasks, ...activeWeekTasks, ...draftTasks];
    const uniqueTasks = Array.from(new Map(allTasks.map(task => [task.id, task])).values());

    const completed = uniqueTasks.filter(task => task.status === 'completed').length;
    const inProgress = uniqueTasks.filter(task => task.status === 'in_progress').length;
    const total = uniqueTasks.length;

    return { completed, inProgress, total };
  };

  const stats = getTaskStats();
  const completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  const getWeeklyReviewStatus = () => {
    if (!week) return null;

    const now = new Date();
    const weekEnd = new Date(week.end_date);
    const isWeekOver = now > weekEnd;
    const isOverdue = isWeekOver && !week.is_review_completed;

    return {
      isCompleted: week.is_review_completed,
      isOverdue,
      shouldGlow: isOverdue || (!week.is_review_completed && isWeekOver)
    };
  };

  const reviewStatus = getWeeklyReviewStatus();

  // Get reflections that have content
  const getFilledReflections = (): ReviewEntry[] => {
    if (!week.reflections || !Array.isArray(week.reflections)) return [];
    return week.reflections.filter(entry => entry.response?.trim());
  };

  const filledReflections = getFilledReflections();

  return (
    <div className="w-full bg-white dark:bg-gray-800 p-4 lg:p-6 space-y-6">

      {/* Task Statistics */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            {formatWeekRange(week)}
          </h3>
          <button
            onClick={onStartWeeklyReview}
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              reviewStatus?.shouldGlow
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white animate-pulse'
                : reviewStatus?.isCompleted
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
            title={reviewStatus?.isCompleted ? 'View weekly review' : 'Start weekly review'}
          >
{reviewStatus?.isCompleted ? 'Week Details' : 'Week Review Needed'}
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Completion Rate</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{completionRate}%</span>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionRate}%` }}
            ></div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="text-center">
              <div className="font-medium text-green-600">{stats.completed}</div>
              <div className="text-gray-500">Completed</div>
            </div>
            <div className="text-center">
              <div className="font-medium text-blue-600">{stats.inProgress}</div>
              <div className="text-gray-500">In Progress</div>
            </div>
            <div className="text-center">
              <div className="font-medium text-gray-600">{stats.total}</div>
              <div className="text-gray-500">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Week Goals */}
      {week.goals.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
            <Target className="w-4 h-4 mr-2" />
            Week Goals
          </h3>
          <div className="space-y-2">
            {week.goals.map((goal, index) => (
              <div
                key={index}
                className="flex items-start space-x-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
              >
                <Star className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{goal}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Priorities */}
      {week.top_priorities.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2" />
            Top Priorities
          </h3>
          <div className="space-y-2">
            {week.top_priorities.map((priority, index) => (
              <div
                key={index}
                className="flex items-start space-x-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg"
              >
                <div className="w-4 h-4 bg-green-600 rounded-full flex-shrink-0 mt-0.5"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{priority}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Reflections */}
      {filledReflections.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
            <MessageSquare className="w-4 h-4 mr-2" />
            Weekly Reflections
          </h3>
          <div className="space-y-3">
            {filledReflections.map((entry, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {entry.prompt}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  &ldquo;{entry.response}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review Notes */}
      {week.review_notes && (
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
            Review Notes
          </h3>
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {week.review_notes}
            </p>
          </div>
        </div>
      )}

      {/* Weekly Review Button */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onStartWeeklyReview}
          className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
            reviewStatus?.shouldGlow
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg animate-pulse'
              : reviewStatus?.isCompleted
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>
{reviewStatus?.isCompleted ? 'View Week Details' :
             reviewStatus?.isOverdue ? 'Week Review Overdue!' : 'Start Weekly Review'}
          </span>
        </button>

        {reviewStatus?.isCompleted && week.review_completed_time && (
          <p className="text-xs text-gray-500 text-center mt-2">
            Completed {parseUTCTimestamp(week.review_completed_time).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Quick Stats */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="text-lg font-bold text-blue-600">{todayTasks.length}</div>
            <div className="text-xs text-gray-500">Today's Tasks</div>
          </div>
          <div>
            <div className="text-lg font-bold text-green-600">{activeWeekTasks.length}</div>
            <div className="text-xs text-gray-500">Week Tasks</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekSummary;
